import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { AlertTriangle } from "lucide-react";

import ClubbieModal from "../Modal/ClubbieModal";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import DatePicker from "../DatePicker/DatePicker";
import TimePicker from "../TimePicker/TimePicker";
import Checkbox from "../Checkbox/Checkbox";
import { fixturesGenerated } from "../../utils/analytics";
import { GENERATION_MODE } from "../../utils/analyticsEvents";
import {
  generateAllFixtures,
  updateTournament,
} from "../../redux/slices/tournamentSlice";

const ALL_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const INPUT_CLASS =
  "w-full h-10 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors";

const LABEL_CLASS = "block text-[14px] font-normal text-clubbie-white";

/**
 * Estimate fixture count for a phase based on its blocks.
 */
function estimatePhaseFixtures(phase) {
  let total = 0;
  for (const block of phase.blocks || []) {
    const teamCount = block.maxTeams || block.teams?.length || 0;
    if (block.type === "GROUP") {
      if (block.fixtureMode === "FIXED_MATCHES" && block.matchesPerTeam) {
        total += Math.ceil((block.matchesPerTeam * teamCount) / 2);
      } else {
        const tff = block.timesToFaceEachOther || 1;
        total += (teamCount * (teamCount - 1) / 2) * tff;
      }
    } else if (block.type === "BRACKET") {
      total += Math.max(teamCount - 1, 1);
    } else if (block.type === "SINGLE_MATCH") {
      total += 1;
    }
  }
  return Math.max(total, 1);
}


function getAllPitchNames(count) {
  return Array.from({ length: count }, (_, i) => `Pitch ${i + 1}`);
}

function timeToMins(timeStr) {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + (m || 0);
}

function parseScheduleDate(d) {
  const str = typeof d === "string" ? d.slice(0, 10) : new Date(d).toISOString().slice(0, 10);
  return new Date(str + "T00:00:00");
}

function toDateString(d) {
  if (!d) return "";
  return typeof d === "string" ? d.slice(0, 10) : new Date(d).toISOString().slice(0, 10);
}

/**
 * Calculate the daily slot capacity for a phase config on a given date.
 */
function getDayCapacity(date, phaseConfig, globalSettings) {
  const { matchSchedule, matchRules, availablePitches, overrideTimeWindows } = phaseConfig;

  const pitchCount = availablePitches?.length || 1;
  const gameDuration = matchRules.gameDuration || 30;
  const segments = matchRules.breaks || 2;
  const breakDuration = matchRules.breakDuration || 5;
  const interval = matchRules.intervalBetweenMatches || 5;

  const matchDurationMins = (gameDuration * segments) + (breakDuration * (segments - 1));
  const slotDuration = matchDurationMins + interval;
  if (slotDuration <= 0) return Infinity;

  const breakMins = globalSettings.breakEnabled
    ? Math.max(0, timeToMins(globalSettings.breakEndTime) - timeToMins(globalSettings.breakStartTime))
    : 0;

  const jsDay = date.getDay();
  const isWeekend = jsDay === 0 || jsDay === 6;
  const dayStart = overrideTimeWindows
    ? (isWeekend ? matchSchedule.weekendStartTime : matchSchedule.weekdayStartTime)
    : (isWeekend ? globalSettings.weekendStartTime : globalSettings.weekdayStartTime);
  const dayEnd = overrideTimeWindows
    ? (isWeekend ? matchSchedule.weekendEndTime : matchSchedule.weekdayEndTime)
    : (isWeekend ? globalSettings.weekendEndTime : globalSettings.weekdayEndTime);

  let availableMins = timeToMins(dayEnd) - timeToMins(dayStart);
  if (breakMins > 0) availableMins = Math.max(0, availableMins - breakMins);

  const slotsPerDay = Math.max(0, Math.floor(availableMins / slotDuration));
  return slotsPerDay * pitchCount;
}

/**
 * Calculate how many fixtures can fit in the configured date range for a phase,
 * accounting for slots already consumed by previous phases on overlapping dates.
 * Returns { capacity, needed, fits }.
 */
function calculatePhaseCapacity(phase, phaseConfig, globalSettings, previousPhasesConsumed) {
  const { matchSchedule } = phaseConfig;
  const { startDate, endDate, matchDays } = matchSchedule;

  if (!startDate || !endDate) return { fits: true, capacity: 0, needed: 0 };

  const needed = estimatePhaseFixtures(phase);
  const cursor = parseScheduleDate(startDate);
  const end = parseScheduleDate(endDate);
  if (isNaN(cursor.getTime()) || isNaN(end.getTime())) return { fits: true, capacity: 0, needed };

  let totalCapacity = 0;

  while (cursor <= end) {
    const jsDay = cursor.getDay();
    const dayName = ALL_DAYS[((jsDay + 6) % 7)];

    if (matchDays.includes(dayName)) {
      const dateKey = cursor.toISOString().slice(0, 10);
      const daySlots = getDayCapacity(cursor, phaseConfig, globalSettings);
      const consumed = previousPhasesConsumed[dateKey] || 0;
      totalCapacity += Math.max(0, daySlots - consumed);
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return { capacity: totalCapacity, needed, fits: totalCapacity >= needed };
}

/**
 * Build a map of date → fixtures consumed by previous phases (before phaseIndex).
 */
function buildConsumedSlotsMap(phases, phaseConfigs, phaseIndex, globalSettings) {
  const consumed = {}; // dateKey -> number of fixtures consuming slots that day

  for (let i = 0; i < phaseIndex; i++) {
    const prevPhase = phases[i];
    const prevConfig = phaseConfigs[i];
    if (!prevConfig?.matchSchedule?.startDate || !prevConfig?.matchSchedule?.endDate) continue;

    const fixtureCount = estimatePhaseFixtures(prevPhase);
    const { matchDays } = prevConfig.matchSchedule;
    const cursor = parseScheduleDate(prevConfig.matchSchedule.startDate);
    const end = parseScheduleDate(prevConfig.matchSchedule.endDate);
    if (isNaN(cursor.getTime()) || isNaN(end.getTime())) continue;

    // Collect valid days and their capacities for this previous phase
    const validDays = [];
    const tempCursor = new Date(cursor.getTime());
    while (tempCursor <= end) {
      const jsDay = tempCursor.getDay();
      const dayName = ALL_DAYS[((jsDay + 6) % 7)];
      if (matchDays.includes(dayName)) {
        const dateKey = tempCursor.toISOString().slice(0, 10);
        const daySlots = getDayCapacity(tempCursor, prevConfig, globalSettings);
        validDays.push({ dateKey, daySlots });
      }
      tempCursor.setDate(tempCursor.getDate() + 1);
    }

    // Distribute the previous phase's fixtures across its valid days
    let remaining = fixtureCount;
    for (const { dateKey, daySlots } of validDays) {
      if (remaining <= 0) break;
      const used = Math.min(remaining, daySlots);
      consumed[dateKey] = (consumed[dateKey] || 0) + used;
      remaining -= used;
    }
  }

  return consumed;
}

/**
 * GenerateFixturesModal — Steps through global settings + each phase to configure timing/schedule, then generates.
 */
const GenerateFixturesModal = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { selectedTournament, selectedCategory } = useSelector(
    (state) => state.tournament
  );

  const phases = useMemo(() => selectedCategory?.phases || [], [selectedCategory]);
  const competitionStart = toDateString(selectedTournament?.matchSchedule?.startDate);
  const competitionEnd = toDateString(selectedTournament?.matchSchedule?.endDate);

  // Hide the match-days (day-of-week) selector when the tournament spans 7
  // days or fewer — every day of the week appears at most once, so filtering
  // is meaningless.
  const tournamentSpanDays = useMemo(() => {
    if (!competitionStart || !competitionEnd) return null;
    const start = new Date(competitionStart);
    const end = new Date(competitionEnd);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
  }, [competitionStart, competitionEnd]);
  const showMatchDaysSelector = tournamentSpanDays === null || tournamentSpanDays > 7;

  const [isLoading, setIsLoading] = useState(false);
  // Step 0 = Global Settings, 1+ = phase steps
  const [currentStep, setCurrentStep] = useState(0);

  // Global settings (Step 0)
  const [globalSettings, setGlobalSettings] = useState({
    numberOfPitches: 3,
    weekdayStartTime: "09:00",
    weekdayEndTime: "21:00",
    weekendStartTime: "09:00",
    weekendEndTime: "17:00",
    breakEnabled: false,
    breakLabel: "",
    breakStartTime: "12:00",
    breakEndTime: "13:00",
  });

  // Per-phase configs
  const [phaseConfigs, setPhaseConfigs] = useState([]);

  // Initialize when modal opens
  useEffect(() => {
    if (isOpen && phases.length > 0) {
      setCurrentStep(0);

      const pitchCount = selectedTournament?.matchVenue?.numberOfPitches || 3;
      // Seed global step from the first phase's previously-saved schedule, since
      // the modal broadcasts globals into every non-overriding phase on submit.
      const seedPhase = phases[0]?.matchSchedule || {};
      setGlobalSettings({
        numberOfPitches: pitchCount,
        weekdayStartTime: seedPhase.startTime || "09:00",
        weekdayEndTime: seedPhase.dayEndTime || "21:00",
        weekendStartTime: seedPhase.weekendStartTime || "09:00",
        weekendEndTime: seedPhase.weekendEndTime || "17:00",
        breakEnabled: seedPhase.breakEnabled ?? false,
        breakLabel: seedPhase.breakLabel || "",
        breakStartTime: seedPhase.breakStartTime || "12:00",
        breakEndTime: seedPhase.breakEndTime || "13:00",
      });

      const allPitches = getAllPitchNames(pitchCount);

      setPhaseConfigs(
        phases.map((phase, idx) => ({
          phaseId: phase._id || phase.id,
          phaseName: phase.name,
          initialized: idx === 0,
          availablePitches: phase.availablePitches?.length > 0 ? [...phase.availablePitches] : [...allPitches],
          matchRules: {
            gameDuration: phase.matchRules?.gameDuration || 30,
            breaks: phase.matchRules?.breaks || 2,
            breakDuration: phase.matchRules?.breakDuration || 5,
            intervalBetweenMatches: phase.matchRules?.intervalBetweenMatches ?? 5,
          },
          matchSchedule: {
            startDate: toDateString(phase.matchSchedule?.startDate) || competitionStart || "",
            endDate: toDateString(phase.matchSchedule?.endDate) || competitionEnd || "",
            matchDays: phase.matchSchedule?.matchDays?.length > 0 ? [...phase.matchSchedule.matchDays] : [...ALL_DAYS],
            weekdayStartTime: phase.matchSchedule?.startTime || "09:00",
            weekdayEndTime: phase.matchSchedule?.dayEndTime || "21:00",
            weekendStartTime: phase.matchSchedule?.weekendStartTime || "09:00",
            weekendEndTime: phase.matchSchedule?.weekendEndTime || "17:00",
          },
          overrideTimeWindows: false,
          duplicateFromPrevious: idx > 0,
        }))
      );
    }
  }, [isOpen, phases, selectedTournament, competitionStart, competitionEnd]);

  // Derived pitch list from global count
  const pitchNames = useMemo(() => getAllPitchNames(globalSettings.numberOfPitches), [globalSettings.numberOfPitches]);

  // When pitch count changes, update phase configs
  useEffect(() => {
    if (!isOpen) return;
    const newPitches = getAllPitchNames(globalSettings.numberOfPitches);
    setPhaseConfigs((prev) =>
      prev.map((config) => {
        const kept = config.availablePitches.filter((p) => newPitches.includes(p));
        const added = newPitches.filter((p) => !config.availablePitches.includes(p));
        return { ...config, availablePitches: [...kept, ...added] };
      })
    );
  }, [globalSettings.numberOfPitches, isOpen]);

  // --- Global settings helpers ---
  const updateGlobal = (field, value) => {
    setGlobalSettings((prev) => ({ ...prev, [field]: value }));
  };

  // --- Phase config helpers ---
  const updatePhaseConfig = (field, value) => {
    setPhaseConfigs((prev) => {
      const updated = [...prev];
      const phaseIdx = currentStep - 1;
      updated[phaseIdx] = { ...updated[phaseIdx], [field]: value };
      return updated;
    });
  };

  const updateMatchRule = (field, value) => {
    setPhaseConfigs((prev) => {
      const updated = [...prev];
      const phaseIdx = currentStep - 1;
      updated[phaseIdx] = {
        ...updated[phaseIdx],
        matchRules: { ...updated[phaseIdx].matchRules, [field]: value },
      };
      return updated;
    });
  };

  const updateSchedule = (field, value) => {
    setPhaseConfigs((prev) => {
      const updated = [...prev];
      const phaseIdx = currentStep - 1;
      updated[phaseIdx] = {
        ...updated[phaseIdx],
        matchSchedule: { ...updated[phaseIdx].matchSchedule, [field]: value },
      };
      return updated;
    });
  };

  const togglePitch = (pitch) => {
    const phaseIdx = currentStep - 1;
    const current = phaseConfigs[phaseIdx]?.availablePitches || [];
    const updated = current.includes(pitch)
      ? current.filter((p) => p !== pitch)
      : [...current, pitch];
    updatePhaseConfig("availablePitches", updated);
  };

  const toggleMatchDay = (day) => {
    const phaseIdx = currentStep - 1;
    const current = phaseConfigs[phaseIdx]?.matchSchedule?.matchDays || [];
    const updated = current.includes(day)
      ? current.filter((d) => d !== day)
      : [...current, day];
    updateSchedule("matchDays", updated);
  };

  const toggleDuplicateFromPrevious = (checked) => {
    const phaseIdx = currentStep - 1;
    const prevPhaseIdx = phaseIdx - 1;
    if (checked && prevPhaseIdx >= 0) {
      const prev = phaseConfigs[prevPhaseIdx];
      setPhaseConfigs((configs) => {
        const updated = [...configs];
        updated[phaseIdx] = {
          ...updated[phaseIdx],
          duplicateFromPrevious: true,
          availablePitches: [...prev.availablePitches],
          matchRules: { ...prev.matchRules },
          matchSchedule: {
            ...updated[phaseIdx].matchSchedule,
            matchDays: [...prev.matchSchedule.matchDays],
            weekdayStartTime: prev.matchSchedule.weekdayStartTime,
            weekdayEndTime: prev.matchSchedule.weekdayEndTime,
            weekendStartTime: prev.matchSchedule.weekendStartTime,
            weekendEndTime: prev.matchSchedule.weekendEndTime,
          },
          overrideTimeWindows: prev.overrideTimeWindows,
        };
        return updated;
      });
    } else {
      updatePhaseConfig("duplicateFromPrevious", false);
    }
  };

  // --- Navigation ---
  const handleNext = () => {
    const nextStep = currentStep + 1;

    if (currentStep === 0) {
      // Carry global settings to all phases (only those not overriding)
      setPhaseConfigs((prev) =>
        prev.map((config) => ({
          ...config,
          matchSchedule: {
            ...config.matchSchedule,
            weekdayStartTime: globalSettings.weekdayStartTime,
            weekdayEndTime: globalSettings.weekdayEndTime,
            weekendStartTime: globalSettings.weekendStartTime,
            weekendEndTime: globalSettings.weekendEndTime,
          },
        }))
      );
    } else if (nextStep <= phaseConfigs.length) {
      // Phase-to-phase carry-forward. Runs when either:
      //   - the next phase has never been visited (initial seeding), or
      //   - the "duplicate from previous" toggle is still on, so the user has
      //     opted in to keep the next phase mirrored.
      // Without the toggle check, edits to stage 1 after stage 2 was already
      // visited would silently fail to propagate.
      const phaseIdx = currentStep - 1;
      const nextPhaseIdx = nextStep - 1;
      const nextPhase = phaseConfigs[nextPhaseIdx];
      if (!nextPhase.initialized || nextPhase.duplicateFromPrevious) {
        const current = phaseConfigs[phaseIdx];
        setPhaseConfigs((prev) => {
          const updated = [...prev];
          updated[nextPhaseIdx] = {
            ...updated[nextPhaseIdx],
            initialized: true,
            availablePitches: [...current.availablePitches],
            matchRules: { ...current.matchRules },
            matchSchedule: {
              ...updated[nextPhaseIdx].matchSchedule,
              matchDays: [...current.matchSchedule.matchDays],
              weekdayStartTime: current.matchSchedule.weekdayStartTime,
              weekdayEndTime: current.matchSchedule.weekdayEndTime,
              weekendStartTime: current.matchSchedule.weekendStartTime,
              weekendEndTime: current.matchSchedule.weekendEndTime,
            },
          };
          return updated;
        });
      }
    }

    setCurrentStep(nextStep);
  };

  const handleBack = () => setCurrentStep((prev) => Math.max(0, prev - 1));

  // --- Generate fixtures ---
  const handleGenerate = async () => {
    if (!selectedTournament || !selectedCategory) {
      toast.error("Please select a tournament and category first");
      return;
    }

    setIsLoading(true);
    const loadingToast = toast.loading("Generating fixtures...");

    try {
      const tournamentId = selectedTournament?.id || selectedTournament?._id;
      const categoryId = selectedCategory?._id || selectedCategory?.id;

      // Persist pitch count if changed
      const currentPitchCount = selectedTournament?.matchVenue?.numberOfPitches || 0;
      if (globalSettings.numberOfPitches !== currentPitchCount) {
        await dispatch(
          updateTournament({
            id: tournamentId,
            updateData: { matchVenue: { numberOfPitches: globalSettings.numberOfPitches } },
          })
        ).unwrap();
      }

      // Build phaseSettings payload
      const phaseSettings = phaseConfigs.map(({ phaseName, initialized, overrideTimeWindows, duplicateFromPrevious, ...rest }) => {
        const schedule = { ...rest.matchSchedule };

        // If phase doesn't override, use global values
        if (!overrideTimeWindows) {
          schedule.weekdayStartTime = globalSettings.weekdayStartTime;
          schedule.weekdayEndTime = globalSettings.weekdayEndTime;
          schedule.weekendStartTime = globalSettings.weekendStartTime;
          schedule.weekendEndTime = globalSettings.weekendEndTime;
        }

        // Map weekday times to legacy startTime/dayEndTime for backward compat
        schedule.startTime = schedule.weekdayStartTime;
        schedule.dayEndTime = schedule.weekdayEndTime;
        // Remove frontend-only keys not in backend schema
        delete schedule.weekdayStartTime;
        delete schedule.weekdayEndTime;

        // Merge global break settings
        schedule.breakEnabled = globalSettings.breakEnabled;
        schedule.breakLabel = globalSettings.breakLabel;
        schedule.breakStartTime = globalSettings.breakStartTime;
        schedule.breakEndTime = globalSettings.breakEndTime;

        return { ...rest, matchSchedule: schedule };
      });

      // Detect the user's timezone so the backend can convert local
      // schedule strings to true UTC instants per fixture.
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      await dispatch(
        generateAllFixtures({ tournamentId, categoryId, phaseSettings, timezone })
      ).unwrap();

      fixturesGenerated({
        generationMode: GENERATION_MODE.AUTO,
        competitionId: tournamentId,
        categoryId,
      });

      toast.dismiss(loadingToast);
      toast.success("Fixtures generated successfully!");
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error?.message || "Failed to generate fixtures");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Computed ---
  const totalSteps = 1 + phases.length; // global + phases
  const isLastStep = currentStep === totalSteps - 1;
  const isGlobalStep = currentStep === 0;
  const phaseConfig = !isGlobalStep ? phaseConfigs[currentStep - 1] : null;
  const currentPhase = !isGlobalStep ? phases[currentStep - 1] : null;

  // Capacity validation for current phase (accounts for previous phases' consumed slots)
  const capacityCheck = useMemo(() => {
    if (!currentPhase || !phaseConfig) return null;
    const phaseIdx = currentStep - 1;
    const consumed = buildConsumedSlotsMap(phases, phaseConfigs, phaseIdx, globalSettings);
    return calculatePhaseCapacity(currentPhase, phaseConfig, globalSettings, consumed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, phaseConfigs, globalSettings]);

  // --- Stepper ---
  const stepLabels = ["Global", ...phases.map((p) => p.name)];

  // --- Footer ---
  const footer = (
    <div className="flex items-center gap-3 w-full">
      <button
        type="button"
        className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
        onClick={onClose}
      >
        Cancel
      </button>
      <div className="ml-auto flex items-center gap-3">
        {currentStep > 0 && (
          <button
            type="button"
            className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
            onClick={handleBack}
          >
            Back
          </button>
        )}
        {isLastStep ? (
          <button
            type="button"
            className="bg-[#00E7FF] hover:opacity-50 text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={handleGenerate}
            disabled={isLoading}
          >
            {isLoading ? <><LoadingSpinner size="small" /><span>Generating...</span></> : "Generate fixtures"}
          </button>
        ) : (
          <button
            type="button"
            className="bg-[#00E7FF] hover:opacity-50 text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all cursor-pointer"
            onClick={handleNext}
          >
            Next
          </button>
        )}
      </div>
    </div>
  );

  if (!phases.length) return null;

  return (
    <ClubbieModal
      isOpen={isOpen}
      width="760px"
      maxHeight="85vh"
      toggle={onClose}
      title="Generate fixtures"
      footer={footer}
    >
      {/* Stepper */}
      {stepLabels.length > 1 && (
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          {stepLabels.map((label, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                  idx < currentStep
                    ? "bg-[#00E7FF] text-[#000F1F]"
                    : idx === currentStep
                      ? "bg-[#00E7FF] text-[#000F1F]"
                      : "bg-[#192634] text-[#7F8585]"
                }`}
              >
                {idx < currentStep ? (
                  <svg className="w-3 h-3" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  idx + 1
                )}
              </div>
              <span className={`text-[13px] font-medium ${idx === currentStep ? "text-[#FEFCEB]" : "text-[#7F8585]"}`}>
                {label}
              </span>
              {idx < stepLabels.length - 1 && <div className="w-4 h-px bg-[#192634]" />}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-4 overflow-y-auto">
        {/* ==================== STEP 0: GLOBAL SETTINGS ==================== */}
        {isGlobalStep && (
          <>
            {/* Pitches */}
            <div className="space-y-2">
              <label className={LABEL_CLASS}>Number of pitches</label>
              <div className="space-y-1">
                {/* <span className="text-xs text-app-text-secondary">Number of pitches</span> */}
                <input
                  type="number"
                  className={INPUT_CLASS}
                  value={globalSettings.numberOfPitches}
                  min="1"
                  onChange={(e) => updateGlobal("numberOfPitches", Math.max(1, parseInt(e.target.value) || 1))}
                  onWheel={(e) => e.target.blur()}
                />
              </div>
            </div>

            <div className="border-t border-[#192634]" />

            {/* Daily time windows — stacked weekdays + weekends */}
            <div className="space-y-2">
              <label className={LABEL_CLASS}>Daily time windows</label>

              <div className="space-y-2">
                <span className="text-xs text-app-text">Weekdays (Mon-Fri)</span>
                <div className="grid grid-cols-2 gap-3">
                  <TimePicker
                    value={globalSettings.weekdayStartTime}
                    onChange={(v) => updateGlobal("weekdayStartTime", v)}
                  />
                  <TimePicker
                    value={globalSettings.weekdayEndTime}
                    onChange={(v) => updateGlobal("weekdayEndTime", v)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <span className="text-xs text-app-text">Weekends (Sat-Sun)</span>
                <div className="grid grid-cols-2 gap-3">
                  <TimePicker
                    value={globalSettings.weekendStartTime}
                    onChange={(v) => updateGlobal("weekendStartTime", v)}
                  />
                  <TimePicker
                    value={globalSettings.weekendEndTime}
                    onChange={(v) => updateGlobal("weekendEndTime", v)}
                  />
                </div>
              </div>
            </div>

            {/* Break Window */}
            <div className="space-y-2">
              <Checkbox
                checked={globalSettings.breakEnabled || false}
                onChange={(e) => updateGlobal("breakEnabled", e.target.checked)}
                label={
                  <>
                    <span className="text-sm text-[#FEFCEB] font-medium">Break Window</span>
                    <span className="text-sm text-app-text-secondary ml-1">
                      (No matches will be scheduled during this window)
                    </span>
                  </>
                }
              />

              {globalSettings.breakEnabled && (
                <div className="space-y-3 pt-1">
                  {/* <div className="space-y-1">
                    <span className="text-xs text-app-text-secondary">Label</span>
                    <input
                      type="text"
                      className={INPUT_CLASS}
                      value={globalSettings.breakLabel}
                      placeholder="e.g. Lunch"
                      onChange={(e) => updateGlobal("breakLabel", e.target.value)}
                    />
                  </div> */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <span className="text-xs text-app-text-secondary">From</span>
                      <TimePicker
                        value={globalSettings.breakStartTime}
                        onChange={(v) => updateGlobal("breakStartTime", v)}
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs text-app-text-secondary">To</span>
                      <TimePicker
                        value={globalSettings.breakEndTime}
                        onChange={(v) => updateGlobal("breakEndTime", v)}
                      />
                    </div>
                  </div>
                  {/* <p className="text-xs text-app-text-secondary">
                    No matches will be scheduled during this window.
                  </p> */}
                </div>
              )}
            </div>
          </>
        )}

        {/* ==================== STEPS 1+: PER-PHASE CONFIG ==================== */}
        {!isGlobalStep && phaseConfig && (
          <div className="space-y-3">

            {/* Capacity Warning */}
            {
            capacityCheck && !capacityCheck.fits && 
            (
              <div className="flex items-start gap-2 rounded-xl py-3 text-sm text-clubbie-yellow">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <span>Estimated {capacityCheck.needed} fixtures but only {capacityCheck.capacity} can fit in the selected date range. Consider extending dates, adding pitches, or adjusting match timings.</span>
              </div>
            )}

            {/* Schedule dates — always editable, not part of duplicate */}
            <div className="space-y-2">
              <label className={LABEL_CLASS}>Schedule</label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <span className="text-[13px] text-clubbie-white">Start Date</span>
                  <DatePicker
                    value={phaseConfig.matchSchedule?.startDate || ""}
                    onChange={(date) => updateSchedule("startDate", date)}
                    minDate={competitionStart}
                    maxDate={competitionEnd}
                    disablePastDates={false}
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-[13px] text-clubbie-white">End Date</span>
                  <DatePicker
                    value={phaseConfig.matchSchedule?.endDate || ""}
                    onChange={(date) => updateSchedule("endDate", date)}
                    minDate={phaseConfig.matchSchedule?.startDate || competitionStart}
                    maxDate={competitionEnd}
                    disablePastDates={false}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-[#192634]" />

            {/* Duplicate from previous stage */}
            {currentStep > 1 && (
              <Checkbox
                checked={phaseConfig.duplicateFromPrevious || false}
                onChange={(e) => toggleDuplicateFromPrevious(e.target.checked)}
                label="Duplicate from previous stage"
              />
            )}

            <div className={`${currentStep > 1 && phaseConfig.duplicateFromPrevious && "opacity-40 pointer-events-none"} space-y-4`}>
              {/* Pitches */}
              <div className="space-y-2">
                <label className={LABEL_CLASS}>Available pitches</label>
                <div className="flex flex-wrap gap-2">
                  {pitchNames.map((pitch) => {
                    const active = phaseConfig.availablePitches?.includes(pitch);
                    return (
                      <button
                        key={pitch}
                        type="button"
                        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          active
                              ? "bg-app-card text-light-blue border border-app-card"
                            : "bg-app-surface text-[#FEFCEB] border border-app-surface hover:bg-[#192634]"
                        }`}
                        onClick={() => togglePitch(pitch)}
                      >
                        {pitch}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="border-t border-[#192634]" />

              {/* Match Timing */}
              <div className="space-y-2">
                <label className={LABEL_CLASS}>Match timings</label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-[13px] text-clubbie-white">Segments per match</span>
                    <input
                      type="number"
                      className={INPUT_CLASS}
                      value={phaseConfig.matchRules?.breaks || ""}
                      min="1"
                      onChange={(e) => updateMatchRule("breaks", parseInt(e.target.value) || 0)}
                      onWheel={(e) => e.target.blur()}
                    />
                  </div>
                  <div className="space-y-1">
                    <span className="text-[13px] text-clubbie-white">Duration per segment (min)</span>
                    <input
                      type="number"
                      className={INPUT_CLASS}
                      value={phaseConfig.matchRules?.gameDuration || ""}
                      min="1"
                      onChange={(e) => updateMatchRule("gameDuration", parseInt(e.target.value) || 0)}
                      onWheel={(e) => e.target.blur()}
                    />
                  </div>
                  <div className="space-y-1">
                    <span className="text-[13px] text-clubbie-white">Time between segments (min)</span>
                    <input
                      type="number"
                      className={INPUT_CLASS}
                      value={phaseConfig.matchRules?.breakDuration || ""}
                      min="0"
                      onChange={(e) => updateMatchRule("breakDuration", parseInt(e.target.value) || 0)}
                      onWheel={(e) => e.target.blur()}
                    />
                  </div>
                  <div className="space-y-1">
                    <span className="text-[13px] text-clubbie-white">Time between matches (min)</span>
                    <input
                      type="number"
                      className={INPUT_CLASS}
                      value={phaseConfig.matchRules?.intervalBetweenMatches ?? ""}
                      min="0"
                      onChange={(e) => updateMatchRule("intervalBetweenMatches", parseInt(e.target.value) || 0)}
                      onWheel={(e) => e.target.blur()}
                    />
                  </div>
                </div>
              </div>

              {/* Remaining schedule settings */}
              <div className="space-y-3">

                {/* Match days */}
                {showMatchDaysSelector && (
                  <div className="space-y-2">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[14px] font-medium text-clubbie-white">Match days</span>
                      <span className="text-[13px] text-app-text-secondary">(the days on which matches should be scheduled)</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {ALL_DAYS.map((day) => {
                        const active = phaseConfig.matchSchedule?.matchDays?.includes(day);
                        return (
                          <button
                            key={day}
                            type="button"
                            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                              active
                                ? "bg-app-card text-light-blue border border-app-card"
                                : "bg-app-surface text-[#FEFCEB] border border-app-surface hover:bg-[#192634]"
                            }`}
                            onClick={() => toggleMatchDay(day)}
                          >
                            {day.slice(0, 3)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="border-t border-[#192634]" />

                {/* Override Daily Time Windows */}
                <div className="space-y-3">
                  <Checkbox
                    checked={phaseConfig.overrideTimeWindows || false}
                    onChange={(e) => updatePhaseConfig("overrideTimeWindows", e.target.checked)}
                    label="Override daily time windows"
                  />
                  {phaseConfig.overrideTimeWindows && (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <span className="text-xs text-app-text-secondary">Weekdays (Mon-Fri)</span>
                        <div className="grid grid-cols-2 gap-3">
                          <TimePicker
                            value={phaseConfig.matchSchedule?.weekdayStartTime || "09:00"}
                            onChange={(v) => updateSchedule("weekdayStartTime", v)}
                          />
                          <TimePicker
                            value={phaseConfig.matchSchedule?.weekdayEndTime || "21:00"}
                            onChange={(v) => updateSchedule("weekdayEndTime", v)}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <span className="text-xs text-app-text-secondary">Weekends (Sat-Sun)</span>
                        <div className="grid grid-cols-2 gap-3">
                          <TimePicker
                            value={phaseConfig.matchSchedule?.weekendStartTime || "09:00"}
                            onChange={(v) => updateSchedule("weekendStartTime", v)}
                          />
                          <TimePicker
                            value={phaseConfig.matchSchedule?.weekendEndTime || "17:00"}
                            onChange={(v) => updateSchedule("weekendEndTime", v)}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        )}
      </div>
    </ClubbieModal>
  );
};

export default GenerateFixturesModal;
